import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";
import { asyncLoadScript } from "../../../utils/common";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import VoicecallNotificationAudio from "./voicecallNotificationAudio";

type ModalVMCameraVoicecallProps = {
  cameraSerialNumber: string;
  onHideCallback?: () => void;
};

const ModalVMCameraVoicecall: FC<ModalVMCameraVoicecallProps> = ({
  cameraSerialNumber,
  onHideCallback,
}) => {
  const { t } = useTranslation();

  const initRef = React.useRef(false);
  const [progressValue, setProgressValue] = React.useState<number>(0);
  const [voicecallError, setVoicecallError] = React.useState<string | null>(
    null
  );

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);

  const webRTCConfig = {
    // "staging" | "prod"
    env: loggedInUser?.config?.VISIONMAX_BASE_URL?.includes("staging")
      ? "staging"
      : "prod",
    // VM_ENV === "prod"
    //   ? process.env.REACT_APP_VISIONMAX_ENV
    //   : process.env.REACT_APP_VISIONMAX_ENV_STAGING,
    apiKey: loggedInUser?.config?.VISIONMAX_X_API_KEY,
    // VM_ENV === "prod"
    //   ? process.env.REACT_APP_VISIONMAX_KEY
    //   : process.env.REACT_APP_VISIONMAX_KEY_STAGING,
    clientAccessToken: localStorage.getItem(
      localStorageKeys.camerasAdminVMToken
    ),
    feature: {
      enableDefaultCameraSwitchButton: false,
    },
  };

  const [voicecallRinging, setVoicecallRinging] =
    React.useState<boolean>(false);

  const [hangUp, setHangUp] = React.useState<boolean>(false);
  const handleHangUp = () => {
    setHangUp(true);
    setTimeout(() => {
      hideModal();
    }, 500);
  };

  useEffect(() => {
    if (!!voicecallError) {
      setVoicecallRinging(false);
    }
  }, [voicecallError]);

  // by default, when the voice call starts, the call is unmuted
  const [voicecallMuted, setVoicecallMuted] = React.useState<boolean>(false);
  const muteUnmuteVoicecall = (mute = true) => {
    const vmRTC = (window as any).vmRTC;
    if (vmRTC) {
      if (mute) {
        vmRTC.disableLocalStreamSender("audio");
        setVoicecallMuted(true);
      } else {
        vmRTC.enableLocalStreamSender("audio");
        setVoicecallMuted(false);
      }
    } else {
      if (APP_CONFIG.DEBUG.ALL)
        console.error("== [muteUnmuteVoicecall] vmRTC is not defined");
    }
  };

  // flag to indicate if the microphone permission was denied
  const [microphonePermissionDenied, setMicrophonePermissionDenied] =
    React.useState<boolean>(false);

  // flag to indicate if the voice call is picked up at the device
  const [voicecallPicked, setVoicecallPicked] = React.useState<boolean>(false);

  // display asset and driver details
  const voiceCallAssetId = modalsState.voiceCallAssetId ?? "";
  const voiceCallDriverName = modalsState.voiceCallDriverName ?? "";

  // event handlers
  const vmRTCEventHandlerEventMapping = {
    progressStatusUpdated: "progressStatusUpdated",
    peer_connected: "peer connected",
    session_stable: "session stable",
    fail_to_get_system_input_devices: "fail to get system input devices",
    requiring_the_permission_of_microphone:
      "requiring the permission of microphone",
    fail_to_require_the_permission_of_microphone:
      "fail to require the permission of microphone",
    required_the_permission_of_microphone:
      "required the permission of microphone",
    call_picked_at_the_device: "call picked at the device",
    not_answered_by_the_device: "not answered by the device",
    camera_not_supported_or_feature_disabled:
      "camera not supported or feature disabled",
    connect_fail: "connect fail",
    connection_lost: "connection lost",
    initial_fail: "initial.fail",
  };
  const vmRTCEventHandlers = {
    progressStatusUpdated: (input: any) => {
      // setProgressValue(parseInt(input, 10));
      console_log("== WEBRTCEVENT [progressStatusUpdated]", input);
      setProgressValue(input);
    },
    peer_connected: () => {
      console_log("== WEBRTCEVENT [peer connected]");
    },
    session_stable: () => {
      const vmRTC = (window as any).vmRTC;
      console_log("== WEBRTCEVENT [session stable]");
      vmRTC.disableLocalStreamSender("audio");
      vmRTC.disableRemoteStreamReceiver("audio");
      setVoicecallMuted(true);
      setVoicecallRinging(true);
    },
    fail_to_get_system_input_devices: () => {
      console_log("== WEBRTCEVENT [fail to get system input devices]");
    },
    requiring_the_permission_of_microphone: () => {
      console_log("== WEBRTCEVENT [requiring the permission of microphone]");
    },
    fail_to_require_the_permission_of_microphone: () => {
      console_log(
        "== WEBRTCEVENT [fail to require the permission of microphone]"
      );
      setMicrophonePermissionDenied(true);
      setVoicecallError("The permission to use microphone was denied");
    },
    required_the_permission_of_microphone: () => {
      console_log("== WEBRTCEVENT [required the permission of microphone]");
    },
    call_picked_at_the_device: () => {
      const vmRTC = (window as any).vmRTC;
      console_log("== WEBRTCEVENT [call picked at the device]");
      setVoicecallPicked(true);
      vmRTC.enableLocalStreamSender("audio");
      vmRTC.enableRemoteStreamReceiver("audio");
      setVoicecallMuted(false);
      setVoicecallRinging(false);
    },
    not_answered_by_the_device: () => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [not answered by the device]");
      setVoicecallError("The call was not answered by the device");
    },
    camera_not_supported_or_feature_disabled: () => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [camera not supported or feature disabled]");
      setVoicecallError("camera not supported or feature disabled");
    },
    connect_fail: (input: any) => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [connect fail]", input);
      setVoicecallError(
        "Connect failed." +
          (microphonePermissionDenied
            ? " Please check your microphone permission."
            : "")
      );
    },
    connection_lost: () => {
      const vmRTC = (window as any).vmRTC;
      vmRTC.stop();
      console_log("== WEBRTCEVENT [connection lost]");
      setVoicecallError("connection lost");
    },
    initial_fail: (input: any) => {
      console_log("== WEBRTCEVENT [initial.fail]", input);
      setVoicecallError("initial fail");
    },
  };

  const initVoicecall = React.useCallback(() => {
    if (initRef.current) return;

    // update the ref to ensure the code executes only once
    initRef.current = true;

    // initialize scripts start here
    console_log("== [initVoicecall]");

    // import the webRTC script from `public/js` directory
    // ---------------------------------------------------
    // other dependencies (aws-sdk-2.1490.0.min.js, mqttws31.js and adapter.js)
    // are already loaded in the index.html file
    // a copy of each of these files is archived in `src/components/visionmax-liveview-ui/archive/` directory
    asyncLoadScript(
      `${process.env.PUBLIC_URL}/js/vmRTC-umd.js`,
      "visionmax-liveview-webrtc",
      () => {
        console_log("== [initVoicecall] webrtc script loaded");
        try {
          // initialize the webRTC
          const vmRTC = (window as any).vmRTC;
          if (vmRTC) {
            // bind event handlers
            for (const eventKey in vmRTCEventHandlerEventMapping) {
              vmRTC.addEventListener(
                vmRTCEventHandlerEventMapping?.[
                  eventKey as keyof typeof vmRTCEventHandlerEventMapping
                ],
                (vmRTCEventHandlers as any)[
                  eventKey
                ] as keyof typeof vmRTCEventHandlers
              );
            }

            // setup and start
            console_log("== [SERIAL_NUMBER]", cameraSerialNumber);
            // vmRTC.setup(webRTCConfig).start(cameraSerialNumber);
            vmRTC
              .setup(webRTCConfig, "audio_call")
              .startCallDevice(cameraSerialNumber);
          } else {
            console.error("== [initVoicecall] vmRTC is not defined");
          }
        } catch (e) {
          if (APP_CONFIG.DEBUG.ALL) console.error("== ERR", e);
        }
      }
    );
  }, [microphonePermissionDenied, cameraSerialNumber]);

  // initialize voice call
  useEffect(() => {
    initVoicecall();
  }, []);

  // remove event listeners on unmount
  useEffect(() => {
    return () => {
      console_log("== [ModalVMCameraVoicecallIncoming] unmounting");
      const vmRTC = (window as any).vmRTC;
      if (vmRTC) {
        // remove event listeners
        for (const eventKey in vmRTCEventHandlerEventMapping) {
          vmRTC.removeEventListener(
            vmRTCEventHandlerEventMapping?.[
              eventKey as keyof typeof vmRTCEventHandlerEventMapping
            ],
            (vmRTCEventHandlers as any)[
              eventKey
            ] as keyof typeof vmRTCEventHandlers
          );
        }
        // stop the session
        vmRTC.stop();
      }
    };
  }, []);

  const hideModal = () => {
    const vmRTC = (window as any).vmRTC;
    if (vmRTC) {
      vmRTC.stop();
    }
    onHideCallback?.();
    dispatch(
      setModalsData({
        ...modalsState,
        showModalVMCameraVoicecallSN: null,
        voiceCallAssetId: null,
        voiceCallDriverName: null,
      })
    );
  };

  const AudioElement = React.useCallback(() => {
    return (
      <audio
        className={`absolute opacity-0 peer-[]:opacity-100 top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-6rem)] z-[1]`}
        controls
        id="rtc_audio"
        autoPlay
      ></audio>
    );
  }, []);

  if (modalsState.showModalVMCameraVoicecallSN === null) return null;

  return (
    <>
      {(!voicecallPicked || voicecallError) && (
        <div
          className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-vmvoicecall-overlay"
          // non-dismissable
          // onClick={hideModal}
        />
      )}
      <div
        id="visionmax-liveview"
        className={
          `fixed transition-all duration-500 ${voicecallPicked && !voicecallError ? "top-[calc(100vh-8rem)]" : "top-1/2"} inset-x-1/2 w-[560px] max-w-full h-48 bg-black/80 overflow-hidden ` +
          "Xbg-white rounded-xl shadow-lg z-vmvoicecall"
        }
        style={{ transform: "translate(-50%, -50%)" }}
      >
        {/* Asset ID and Driver Name */}
        {(!!voiceCallAssetId || !!voiceCallDriverName) && (
          <div className="absolute top-4 left-4 z-50 text-white text-sm font-semibold space-y-1">
            <div>
              {t("Asset ID")}: {voiceCallAssetId}
            </div>
            <div>
              {t("Driver")}: {voiceCallDriverName}
            </div>
          </div>
        )}
        {/* Camera Buttons Area */}
        <div
          className={`absolute ${voicecallError || progressValue < 100 ? "top-4 right-4" : "top-2/3 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2"} flex flex-row gap-2 z-50 p-2 bg-white/20 rounded-full`}
        >
          {!voicecallError && progressValue === 100 && (
            <>
              {/* Status indicator */}
              <span
                className={`size-4 m-3 rounded-full ${voicecallPicked ? "bg-green-500" : "bg-amber-500/50"}`}
              />

              {/* unmute / mute buttons */}
              <button
                key={`btn_unmute_${cameraSerialNumber}`}
                className={
                  "h-10 flex items-center justify-center rounded-full enabled:focus:ring enabled:focus:ring-white outline-0 " +
                  "enabled:bg-white/50 enabled:hover:bg-white disabled:bg-black/25 disabled:text-white/25 " +
                  "px-4 uppercase font-bold"
                }
                onClick={() => {
                  muteUnmuteVoicecall(false);
                }}
                disabled={
                  !progressValue ||
                  progressValue < 100 ||
                  !voicecallPicked ||
                  voicecallMuted
                }
              >
                {t("unmute")}
              </button>
              <button
                key={`btn_mute_${cameraSerialNumber}`}
                className={
                  "h-10 flex items-center justify-center rounded-full enabled:focus:ring enabled:focus:ring-white outline-0 " +
                  "enabled:bg-white/50 enabled:hover:bg-white disabled:bg-black/25 disabled:text-white/25 " +
                  "px-4 uppercase font-bold"
                }
                onClick={() => {
                  muteUnmuteVoicecall(true);
                }}
                disabled={
                  !progressValue ||
                  progressValue < 100 ||
                  !voicecallPicked ||
                  !voicecallMuted
                }
              >
                {t("mute")}
              </button>

              {/* separator */}
              <div className="w-px h-10 bg-white/15" />
            </>
          )}

          {/* Close / Hang Up Button */}
          {true && ( // (progressValue === 100 || voicecallError)
            <button
              className={
                `group flex items-center justify-center rounded-full focus:ring focus:ring-white uppercase font-bold ` +
                `${
                  voicecallPicked && !voicecallError
                    ? "h-10 px-4 bg-field-error-dark text-white hover:bg-field-error-dark/75 focus:bg-field-error-dark/90"
                    : "size-10 bg-black hover:bg-gray-100"
                } outline-0 whitespace-nowrap`
              }
              onClick={handleHangUp}
            >
              {voicecallPicked && !voicecallError ? (
                <>{t("hangup")}</>
              ) : (
                <img
                  src={svgIcons.CloseIcon}
                  alt={voicecallPicked ? "Hangup" : "Close"}
                  className="grayscale-0 brightness-0 invert group-hover:invert-0 opacity-50"
                />
              )}
            </button>
          )}
        </div>

        {/* Progress bar or error message display */}
        {!!voicecallError ? (
          <p className="absolute z-[2] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg uppercase font-bold text-gray-200/70 text-center">
            {voicecallError}
          </p>
        ) : progressValue < 100 ? (
          <div className="absolute z-[2] top-0 left-0 w-full h-full flex items-center justify-center">
            <div
              className="w-[calc(100%-6rem)] border border-gray-400/70 rounded-md h-12 relative translate-y-6"
              role="progressbar"
              aria-label={`loading progress`}
              aria-valuemin={0}
              aria-valuenow={progressValue}
              aria-valuemax={100}
            >
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -mt-16 text-lg uppercase font-bold text-gray-200/70 text-center">
                {t("connecting")}...
              </span>
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-200/70">
                {progressValue}%
              </span>
              <div
                className="absolute top-0 left-0 h-full bg-gray-400/25"
                style={{ width: `${progressValue}%` }}
              ></div>
            </div>
          </div>
        ) : null}

        {/* Video element */}
        {progressValue === 100 && voicecallPicked && (
          <span className={`peer`}></span> // when this DOM element renders, the audio element is displayed
        )}
        {!voicecallError && AudioElement()}
        {/* <AudioElement /> */}

        {/* Incoming voicecall audio */}
        {voicecallRinging && <VoicecallNotificationAudio type="ringing" />}
        {!!hangUp && <VoicecallNotificationAudio type="hangup" />}
      </div>
    </>
  );
};

function console_log(...params: any[]) {
  if (APP_CONFIG.DEBUG.ALL) {
    console.log(params[0], ...params.slice(1));
  }
}

export default ModalVMCameraVoicecall;
